<template>
  <section class="vp-card" data-profile-card="categories">
    <div
      class="vp-card-head"
      :class="{ 'pv-accordion-head': collapsible }"
      :role="collapsible ? 'button' : null"
      :tabindex="collapsible ? 0 : null"
      :aria-expanded="collapsible ? isExpanded : null"
      @click="toggleCard"
      @keydown.enter.self.prevent="toggleCard"
      @keydown.space.self.prevent="toggleCard"
    >
      <h6 class="vp-card-title">
        <i class="ph-duotone ph-tag"></i>
        {{ t('profile.sections.categories') }}
        <span v-if="categoryTags.length" class="vp-chip">{{ $formatInteger(categoryTags.length) }}</span>
      </h6>
      <div class="d-flex align-items-center gap-2">
        <button
          v-if="canManageCategoryTags"
          type="button"
          class="vp-btn vp-btn-ghost vp-btn-sm"
          @click.stop="openModal"
        >
          <i class="ph-duotone ph-plus"></i>
          {{ t('common.actions.add') }}
        </button>
        <i v-if="collapsible" class="ph-duotone pv-accordion-caret" :class="isExpanded ? 'ph-caret-up' : 'ph-caret-down'"></i>
      </div>
    </div>

    <div v-show="isExpanded" class="vp-card-body">
      <div v-if="isLoading" class="vp-loading">
        <span class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">{{ t('common.loading') }}</span>
        </span>
      </div>

      <div v-else-if="error" class="vp-note vp-note--danger" role="alert">
        {{ error }}
      </div>

      <div v-else-if="!categoryTags.length" class="vp-empty">
        <i class="ph-duotone ph-tag vp-empty-icon"></i>
        <div class="vp-empty-text">{{ t('profile.categoryTags.empty') }}</div>
      </div>

      <template v-else>
        <!--
          Expand/collapse all: with a few dozen categories across three levels, opening
          each branch by hand to see what is selected is the slow part.
        -->
        <div class="d-flex justify-content-end mb-2">
          <button type="button" class="vp-btn vp-btn-ghost vp-btn-sm" @click="toggleExpandAll">
            <i class="ph-duotone" :class="allTagsExpanded ? 'ph-arrows-in-line-vertical' : 'ph-arrows-out-line-vertical'"></i>
            {{ allTagsExpanded ? t('profile.categoryTags.collapseAll') : t('profile.categoryTags.expandAll') }}
          </button>
        </div>

        <!--
          Indentation is applied to the row, not inside the badge: previously each row was
          full-width with a margin on its contents, so the box started at the left edge
          while the text sat somewhere to the right and depth read as stray whitespace.
          The guide rail draws the parent-child relation the indent alone never showed.
        -->
        <ul class="list-unstyled category-tree my-0">
          <li
            v-for="item in visibleCategoryTagRows"
            :key="`${item.materialCategoryId}-${item.depth}`"
            class="category-tree-row"
            :class="{ 'is-root': item.depth === 0 }"
            :style="{ '--depth': item.depth }"
          >
            <button
              v-if="item.hasChildren"
              type="button"
              class="tree-toggle"
              :aria-expanded="isTagExpanded(item.materialCategoryId)"
              @click="toggleTagExpand(item.materialCategoryId)"
            >
              <i class="ph-duotone" :class="isTagExpanded(item.materialCategoryId) ? 'ph-caret-down' : 'ph-caret-right'"></i>
            </button>
            <span v-else class="tree-toggle-placeholder" aria-hidden="true"></span>

            <span class="tree-label">
              <i class="ph-duotone tree-icon" :class="item.hasChildren ? 'ph-folder' : 'ph-tag'"></i>
              <span class="tree-name">{{ item.materialCategoryName || '-' }}</span>
              <span v-if="item.materialCategoryCode" class="tree-code">{{ item.materialCategoryCode }}</span>
            </span>
          </li>
        </ul>
      </template>
    </div>

    <div id="categoryTagsModal" class="modal fade" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content vp-modal">
          <div class="vp-modal-head">
            <h5 class="vp-modal-title">
              <i class="ph-duotone ph-tag"></i>{{ t('profile.categoryTags.selectTitle') }}
            </h5>
            <button type="button" class="vp-modal-close" :aria-label="t('common.close')" @click="closeModal">
              <i class="ph-duotone ph-x"></i>
            </button>
          </div>
          <div class="modal-body p-0">
            <div class="tags-modal-search">
              <div class="tags-filter">
                <i class="ph-duotone ph-magnifying-glass tags-filter-icon"></i>
                <input
                  v-model="searchQuery"
                  type="text"
                  :aria-label="t('profile.categoryTags.searchPlaceholder')"
                  :placeholder="t('profile.categoryTags.searchPlaceholder')"
                />
              </div>
            </div>

            <div class="selection-list">
              <div v-if="isLoadingOptions" class="text-center py-4">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">{{ t('common.loading') }}</span>
                </div>
              </div>

              <div v-else-if="!treeRows.length" class="text-center py-4 text-muted">
                {{ t('common.noResults') }}
              </div>

              <div
                v-for="row in treeRows"
                :key="row.id"
                class="selection-item"
                :class="{ selected: selectedCategoryIds.includes(row.id) }"
              >
                <button
                  v-if="row.isHaveChild"
                  type="button"
                  class="tree-toggle me-2"
                  :style="{ marginLeft: `${row.depth * 18}px` }"
                  @click="toggleNode(row)"
                >
                  <i class="ph-duotone" :class="isNodeExpanded(row.id) ? 'ph-caret-down' : 'ph-caret-right'"></i>
                </button>
                <span v-else class="tree-spacer" :style="{ marginLeft: `${row.depth * 18 + 22}px` }"></span>
                <input
                  type="checkbox"
                  class="form-check-input me-3"
                  :checked="selectedCategoryIds.includes(row.id)"
                  @change="onCategoryToggle(row, $event.target.checked)"
                />
                <div class="d-flex flex-column">
                  <span class="fw-medium">{{ row.name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="vp-modal-foot">
            <span class="vp-modal-foot-gap vp-text-muted small">
              {{ $formatInteger(selectedCategoryIds.length) }} {{ t('profile.categoryTags.selectedCount') }}
            </span>
            <button type="button" class="vp-btn vp-btn-ghost" :disabled="isSaving" @click="closeModal">
              {{ t('common.cancel') }}
            </button>
            <button type="button" class="vp-btn vp-btn-primary" :disabled="isSaving" @click="saveTags">
              <span v-if="isSaving" class="spinner-border spinner-border-sm"></span>
              {{ t('common.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import materialCategoryService from '@/services/materialCategoryService';
import tenantVsCategoryService from '@/services/tenantVsCategoryService';

export default {
  name: 'ProfileCategoryTagsCard',
  emits: ['categories-updated'],
  props: {
    /**
     * Whether the head toggles the body and the card joins the accordion. Off where the
     * card stands alone (its own profile tab), where collapsing would just hide the page.
     */
    collapsible: {
      type: Boolean,
      default: true
    },
    canManageCategoryTags: {
      type: Boolean,
      default: false
    },
    targetTenantId: {
      type: String,
      default: ''
    },
    /**
     * Start open. The profile page stacks these cards as an accordion where collapsed is
     * right; on the dedicated Categories page the card is the whole screen, so opening it
     * closed would hide everything behind a click.
     */
    defaultExpanded: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const ACCORDION_EVENT = 'profile-left-card-open';
    const cardKey = 'categories';
    const isExpanded = ref(!props.collapsible || props.defaultExpanded);

    const isLoading = ref(false);
    const isSaving = ref(false);
    const isLoadingOptions = ref(false);
    const error = ref(null);
    const categoryTags = ref([]);
    const categoryTree = ref([]);
    const selectedCategoryIds = ref([]);
    const searchQuery = ref('');
    const expandedNodeIds = ref(new Set());
    const loadingNodeIds = ref(new Set());
    const expandedTagIds = ref(new Set());
    const modalSelectionSnapshot = ref([]);
    const restoreSelectionOnModalHide = ref(true);

    const currentUser = computed(() => store.getters['auth/currentUser']);
    const effectiveTenantId = computed(() => props.targetTenantId || currentUser.value?.tenantId || null);
    const isOwnProfile = computed(() => {
      if (!effectiveTenantId.value || !currentUser.value?.tenantId) return true;
      return String(effectiveTenantId.value).toUpperCase() === String(currentUser.value.tenantId).toUpperCase();
    });

    let modalInstance = null;
    let modalElementRef = null;
    let searchTimeout = null;
    const handleModalHidden = () => {
      if (restoreSelectionOnModalHide.value) {
        selectedCategoryIds.value = [...modalSelectionSnapshot.value];
      }
      restoreSelectionOnModalHide.value = true;
    };

    const onOtherCardOpened = (event) => {
      if (props.collapsible && event?.detail?.card !== cardKey) {
        isExpanded.value = false;
      }
    };

    const toggleCard = () => {
      if (!props.collapsible) return;
      if (isExpanded.value) {
        isExpanded.value = false;
        return;
      }
      window.dispatchEvent(new CustomEvent(ACCORDION_EVENT, { detail: { card: cardKey } }));
      isExpanded.value = true;
    };

    function extractApiErrorMessage(err, fallbackMessage) {
      const responseData = err?.response?.data;
      const apiError = responseData?.error;
      const validationMessages = Array.isArray(apiError?.validationErrors)
        ? apiError.validationErrors
            .map((v) => v?.message)
            .filter((m) => typeof m === 'string' && m.trim().length > 0)
        : [];

      if (validationMessages.length) return validationMessages.join(' ');
      if (typeof apiError?.details === 'string' && apiError.details.trim()) return apiError.details.trim();
      if (typeof apiError?.message === 'string' && apiError.message.trim()) return apiError.message.trim();
      if (typeof responseData?.message === 'string' && responseData.message.trim()) return responseData.message.trim();
      return fallbackMessage;
    }

    function normalizeTenantCategory(item) {
      return {
        id: item?.id || `${item?.tenantId || 't'}-${item?.materialCategoryId || 'c'}`,
        materialCategoryId: item?.materialCategoryId || '',
        materialCategoryCode: item?.materialCategoryCode || '',
        materialCategoryName: item?.materialCategoryName || '',
        parentCategoryId: item?.parentCategoryId || null
      };
    }

    function flattenTenantCategoryTree(items) {
      const result = [];

      function visit(nodes) {
        if (!Array.isArray(nodes)) return;
        for (const node of nodes) {
          result.push(normalizeTenantCategory(node));
          if (Array.isArray(node?.children) && node.children.length > 0) {
            visit(node.children);
          }
        }
      }

      visit(items);
      return result;
    }

    async function loadTaggedCategories() {
      isLoading.value = true;
      error.value = null;
      try {
        let list = [];

        try {
          const response = await tenantVsCategoryService.searchTenantCategories({
            tenantId: effectiveTenantId.value,
            page: 0,
            pageSize: 200
          });
          if (Array.isArray(response?.items)) {
            list = response.items;
          } else if (Array.isArray(response)) {
            list = response;
          } else {
            list = [];
          }
        } catch (searchError) {
          if (isOwnProfile.value) {
            const response = await tenantVsCategoryService.getMyCategories();
            list = Array.isArray(response) ? response : [];
          } else {
            const response = await tenantVsCategoryService.getTenantCategories(effectiveTenantId.value);
            list = Array.isArray(response) ? response : [];
          }
        }

        categoryTags.value = flattenTenantCategoryTree(list);
        selectedCategoryIds.value = categoryTags.value.map((x) => x.materialCategoryId).filter(Boolean);
        initializeTagExpansion();
      } catch (err) {
        error.value = extractApiErrorMessage(err, t('profile.categoryTags.loadError'));
      } finally {
        isLoading.value = false;
      }
    }

    function normalizeCategoryNode(item) {
      return {
        id: item.id,
        name: item.name || '',
        code: item.code || '',
        parentId: item.parentId || null,
        isHaveChild: item.isHaveChild === true,
        childrenLoaded: false,
        children: []
      };
    }

    function buildNodeMap(nodes, map = new Map()) {
      for (const node of nodes) {
        map.set(node.id, node);
        if (node.children?.length) {
          buildNodeMap(node.children, map);
        }
      }
      return map;
    }

    function getAncestorIds(nodeId) {
      const nodeMap = buildNodeMap(categoryTree.value);
      const ancestors = [];
      let current = nodeMap.get(nodeId);
      let guard = 0;
      while (current?.parentId && guard < 50) {
        ancestors.push(current.parentId);
        current = nodeMap.get(current.parentId);
        guard += 1;
      }
      return ancestors;
    }

    function getDescendantIds(node) {
      if (!node || !Array.isArray(node.children) || node.children.length === 0) {
        return [];
      }

      const ids = [];
      const stack = [...node.children];

      while (stack.length) {
        const current = stack.pop();
        if (!current?.id) continue;
        ids.push(current.id);
        if (Array.isArray(current.children) && current.children.length) {
          stack.push(...current.children);
        }
      }

      return ids;
    }

    function onCategoryToggle(row, checked) {
      const next = new Set(selectedCategoryIds.value);
      if (checked) {
        next.add(row.id);
        const ancestors = getAncestorIds(row.id);
        for (const id of ancestors) {
          next.add(id);
        }
      } else {
        next.delete(row.id);
        const node = findNode(categoryTree.value, row.id);
        const descendantIds = getDescendantIds(node);
        for (const id of descendantIds) {
          next.delete(id);
        }
      }
      selectedCategoryIds.value = [...next];
    }

    function isNodeExpanded(id) {
      return expandedNodeIds.value.has(id);
    }

    function findNode(nodes, id) {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children?.length) {
          const found = findNode(node.children, id);
          if (found) return found;
        }
      }
      return null;
    }

    async function loadNodeChildren(node) {
      if (!node?.isHaveChild || node.childrenLoaded || loadingNodeIds.value.has(node.id)) {
        return;
      }
      loadingNodeIds.value.add(node.id);
      try {
        const children = await materialCategoryService.getChildCategories(node.id);
        node.children = (Array.isArray(children) ? children : []).map(normalizeCategoryNode);
        node.childrenLoaded = true;
      } finally {
        loadingNodeIds.value.delete(node.id);
      }
    }

    async function toggleNode(row) {
      const node = findNode(categoryTree.value, row.id);
      if (!node) return;

      if (!isNodeExpanded(node.id)) {
        await loadNodeChildren(node);
        expandedNodeIds.value.add(node.id);
        return;
      }
      expandedNodeIds.value.delete(node.id);
    }

    async function loadCategoryTreeRoots() {
      isLoadingOptions.value = true;
      try {
        const response = await materialCategoryService.searchCategories({
          searchText: '',
          page: 1,
          pageSize: 200,
          sorting: 'name asc',
          parentId: null
        });
        const items = Array.isArray(response?.items) ? response.items : [];
        categoryTree.value = items.map(normalizeCategoryNode);
        expandedNodeIds.value = new Set();
      } catch (err) {
        store.dispatch('notify', {
          message: t('profile.categoryTags.loadOptionsError'),
          type: 'error'
        });
      } finally {
        isLoadingOptions.value = false;
      }
    }

    async function openModal() {
      if (!props.canManageCategoryTags || !isOwnProfile.value) return;
      modalSelectionSnapshot.value = [...selectedCategoryIds.value];
      restoreSelectionOnModalHide.value = true;
      await loadCategoryTreeRoots();
      modalInstance?.show();
    }

    function closeModal() {
      if (restoreSelectionOnModalHide.value) {
        selectedCategoryIds.value = [...modalSelectionSnapshot.value];
      }
      modalInstance?.hide();
    }

    async function saveTags() {
      if (!props.canManageCategoryTags || !isOwnProfile.value) return;
      isSaving.value = true;
      error.value = null;
      try {
        const uniqueIds = [...new Set(selectedCategoryIds.value.filter(Boolean))];
        const response = await tenantVsCategoryService.updateMyCategories(uniqueIds);
        const list = Array.isArray(response) ? response : [];
        categoryTags.value = flattenTenantCategoryTree(list);
        selectedCategoryIds.value = categoryTags.value.map((x) => x.materialCategoryId).filter(Boolean);
        initializeTagExpansion();

        store.dispatch('notify', {
          message: t('profile.categoryTags.saveSuccess'),
          type: 'success'
        });
        emit('categories-updated');
        modalSelectionSnapshot.value = [...selectedCategoryIds.value];
        restoreSelectionOnModalHide.value = false;
        closeModal();
      } catch (err) {
        const message = extractApiErrorMessage(err, t('profile.categoryTags.saveError'));
        error.value = message;
        store.dispatch('notify', { message, type: 'error' });
      } finally {
        isSaving.value = false;
      }
    }

    const treeNodeMatches = (node, query) => {
      if (!query) return true;
      return node.name.toLowerCase().includes(query) || node.code.toLowerCase().includes(query);
    };

    const treeNodeHasMatchingDescendant = (node, query) => {
      if (!node.children?.length) return false;
      return node.children.some(
        (child) => treeNodeMatches(child, query) || treeNodeHasMatchingDescendant(child, query)
      );
    };

    const treeRows = computed(() => {
      const query = searchQuery.value.trim().toLowerCase();
      const rows = [];

      const visit = (nodes, depth) => {
        for (const node of nodes) {
          const selfMatch = treeNodeMatches(node, query);
          const childMatch = treeNodeHasMatchingDescendant(node, query);
          if (query && !selfMatch && !childMatch) continue;

          rows.push({ id: node.id, name: node.name, code: node.code, isHaveChild: node.isHaveChild, depth });

          const showChildren = query ? true : isNodeExpanded(node.id);
          if (showChildren && node.children?.length) {
            visit(node.children, depth + 1);
          }
        }
      };

      visit(categoryTree.value, 0);
      return rows;
    });

    const sortByCodeThenName = (a, b) => {
      const codeCompare = String(a.materialCategoryCode || '').localeCompare(String(b.materialCategoryCode || ''));
      if (codeCompare !== 0) return codeCompare;
      return String(a.materialCategoryName || '').localeCompare(String(b.materialCategoryName || ''));
    };

    const buildCategoryTagTree = (items) => {
      const byCategoryId = new Map();
      const childrenByParentId = new Map();

      for (const item of items) {
        byCategoryId.set(item.materialCategoryId, item);
      }
      for (const item of items) {
        const parentId = item.parentCategoryId;
        if (!parentId || !byCategoryId.has(parentId)) continue;
        const arr = childrenByParentId.get(parentId) || [];
        arr.push(item);
        childrenByParentId.set(parentId, arr);
      }

      const roots = items.filter(
        (item) => !item.parentCategoryId || !byCategoryId.has(item.parentCategoryId)
      );
      roots.sort(sortByCodeThenName);
      for (const [parentId, list] of childrenByParentId.entries()) {
        list.sort(sortByCodeThenName);
        childrenByParentId.set(parentId, list);
      }
      return { roots, childrenByParentId };
    };

    const categoryTagRows = computed(() => {
      if (!Array.isArray(categoryTags.value) || categoryTags.value.length === 0) return [];

      const { roots, childrenByParentId } = buildCategoryTagTree(categoryTags.value);
      const rows = [];
      const visited = new Set();
      const parentIds = new Set(
        categoryTags.value
          .map((item) => item.parentCategoryId)
          .filter((id) => typeof id === 'string' && id.length > 0)
      );

      const walk = (node, depth) => {
        if (!node?.materialCategoryId || visited.has(node.materialCategoryId)) return;
        visited.add(node.materialCategoryId);
        rows.push({ ...node, depth, hasChildren: parentIds.has(node.materialCategoryId) });
        const children = childrenByParentId.get(node.materialCategoryId) || [];
        for (const child of children) walk(child, depth + 1);
      };

      for (const root of roots) walk(root, 0);

      for (const item of categoryTags.value) {
        if (!visited.has(item.materialCategoryId)) walk(item, 0);
      }

      return rows;
    });

    const visibleCategoryTagRows = computed(() => {
      const visible = [];
      const expanded = expandedTagIds.value;
      const parentStack = [];

      for (const row of categoryTagRows.value) {
        while (parentStack.length > row.depth) {
          parentStack.pop();
        }

        const parentVisible = parentStack.every((parentId) => expanded.has(parentId));
        if (parentVisible) {
          visible.push(row);
        }

        if (row.hasChildren) {
          parentStack[row.depth] = row.materialCategoryId;
        }
      }

      return visible;
    });

    function initializeTagExpansion() {
      expandedTagIds.value = new Set();
    }

    function isTagExpanded(id) {
      return expandedTagIds.value.has(id);
    }

    // Only rows that actually have children can be expanded, so "all expanded" is
    // measured against those rather than against every row.
    const expandableTagIds = computed(() =>
      categoryTagRows.value.filter((row) => row.hasChildren).map((row) => row.materialCategoryId)
    );

    const allTagsExpanded = computed(() => {
      const ids = expandableTagIds.value;
      return ids.length > 0 && ids.every((id) => expandedTagIds.value.has(id));
    });

    function toggleExpandAll() {
      expandedTagIds.value = allTagsExpanded.value ? new Set() : new Set(expandableTagIds.value);
    }

    function toggleTagExpand(id) {
      const next = new Set(expandedTagIds.value);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      expandedTagIds.value = next;
    }

    watch(searchQuery, () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {}, 200);
    });

    onMounted(() => {
      window.addEventListener(ACCORDION_EVENT, onOtherCardOpened);
      loadTaggedCategories();
      if (typeof bootstrap !== 'undefined') {
        modalElementRef = document.getElementById('categoryTagsModal');
        if (modalElementRef) {
          modalInstance = new bootstrap.Modal(modalElementRef);
          modalElementRef.addEventListener('hidden.bs.modal', handleModalHidden);
        }
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener(ACCORDION_EVENT, onOtherCardOpened);
      if (modalElementRef) {
        modalElementRef.removeEventListener('hidden.bs.modal', handleModalHidden);
      }
    });

    return {
      t,
      isLoading,
      isSaving,
      isExpanded,
      isLoadingOptions,
      error,
      categoryTags,
      categoryTagRows,
      visibleCategoryTagRows,
      treeRows,
      selectedCategoryIds,
      searchQuery,
      isTagExpanded,
      toggleTagExpand,
      allTagsExpanded,
      toggleExpandAll,
      toggleCard,
      toggleNode,
      isNodeExpanded,
      onCategoryToggle,
      openModal,
      closeModal,
      saveTags
    };
  }
};
</script>

<style scoped>
/* Collapsible VESPROC card head — the profile page stacks these as an accordion. */
.pv-accordion-head {
  cursor: pointer;
  user-select: none;
}

.pv-accordion-head:focus-visible {
  outline: 0;
  box-shadow: inset 0 0 0 2px rgba(var(--vp-primary-rgb), 0.25);
}

.pv-accordion-caret {
  font-size: 0.95rem;
  color: var(--vp-gray-500);
}

/* --- Selection modal --- */
.tags-modal-search {
  padding: 12px 18px;
  border-bottom: 1px solid var(--vp-gray-200);
}

.tags-filter {
  position: relative;
  display: flex;
  align-items: center;
}

.tags-filter-icon {
  position: absolute;
  left: 10px;
  font-size: 0.9rem;
  color: var(--vp-gray-500);
  pointer-events: none;
}

.tags-filter input {
  width: 100%;
  font: inherit;
  font-size: 0.8125rem;
  border: 1px solid var(--vp-gray-300);
  border-radius: var(--vp-radius);
  padding: 7px 12px 7px 32px;
  color: var(--vp-body-color);
  background: var(--vp-surface);
  outline: none;
}

.tags-filter input:focus {
  border-color: var(--vp-primary);
  box-shadow: 0 0 0 3px rgba(var(--vp-primary-rgb), 0.12);
}

.selection-list {
  max-height: 380px;
  overflow-y: auto;
}

.selection-item {
  display: flex;
  align-items: center;
  padding: 9px 18px;
  font-size: 0.8125rem;
  color: var(--vp-body-color);
  border-bottom: 1px solid var(--vp-gray-200);
  border-left: 3px solid transparent;
  cursor: pointer;
}

.selection-item:hover {
  background: var(--vp-gray-100);
}

.selection-item.selected {
  background: rgba(var(--vp-primary-rgb), 0.06);
  border-left-color: var(--vp-primary);
}

.selection-item .form-check-input:checked {
  background-color: var(--vp-primary);
  border-color: var(--vp-primary);
}

.tree-spacer {
  width: 1rem;
  display: inline-block;
}

/* --- Selected category tree --- */

.category-tree {
  border: 1px solid var(--vp-gray-300);
  border-radius: var(--vp-radius);
  overflow: hidden;
}

.category-tree-row {
  --indent: calc(var(--depth) * 1.5rem);
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem 0.5rem calc(0.75rem + var(--indent));
  font-size: 0.8125rem;
  color: var(--vp-body-color);
  border-top: 1px solid var(--vp-gray-200);
}

.category-tree-row:first-child {
  border-top: 0;
}

/* Roots carry the weight so the eye can find the top of each branch at a glance. */
.category-tree-row.is-root {
  background: var(--vp-gray-100);
  font-weight: 600;
  color: var(--vp-heading-color);
}

/*
  Guide rail: a short vertical stub at the row's own indent. Nesting was previously only
  implied by how far the text happened to sit from the edge, which is unreadable past the
  first level.
*/
.category-tree-row:not(.is-root)::before {
  content: '';
  position: absolute;
  left: calc(0.75rem + var(--indent) - 0.75rem);
  top: 0;
  bottom: 0;
  border-left: 1px solid var(--vp-gray-300);
}

.category-tree-row:hover {
  background: rgba(var(--vp-primary-rgb), 0.04);
}

.tree-toggle {
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: none;
  color: var(--vp-gray-500);
  cursor: pointer;
}

.tree-toggle:hover {
  background: rgba(var(--vp-primary-rgb), 0.1);
  color: var(--vp-primary);
}

/* Keeps leaf labels on the same axis as expandable ones. */
.tree-toggle-placeholder {
  flex-shrink: 0;
  width: 1.25rem;
}

.tree-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.tree-icon {
  color: var(--vp-gray-500);
  flex-shrink: 0;
}

.category-tree-row.is-root .tree-icon {
  color: var(--vp-primary);
}

.tree-name {
  word-break: break-word;
}

/* Secondary by design: the code is for disambiguation, not for scanning. */
.tree-code {
  font-size: 0.68rem;
  font-weight: 500;
  color: var(--vp-gray-600);
  background: var(--vp-gray-200);
  border-radius: 4px;
  padding: 0.05rem 0.35rem;
  flex-shrink: 0;
}

/* Deep trees would otherwise push labels off the right edge on narrow screens. */
@media (max-width: 575.98px) {
  .category-tree-row {
    --indent: calc(var(--depth) * 0.85rem);
  }
}
</style>
